/**
 * ClickHub - Configuração global de autenticação HTTP
 *
 * ACHADO DA REVISÃO: o backend passou a exigir token de autenticação
 * (Bearer) em mais de 150 endpoints, mas o frontend nunca anexava esse
 * token em nenhuma chamada - existia um helper pronto para isso
 * (authService.fetchAuth), mas nada no app o usava de fato. Isso faria
 * praticamente todo o sistema responder 401 (não autorizado) contra o
 * backend corrigido.
 *
 * Em vez de reescrever individualmente as ~20 chamadas de fetch/axios
 * espalhadas por muitos arquivos (arriscado de revisar de uma vez e lento
 * de fazer com segurança), esta configuração central resolve isso num
 * único lugar: qualquer chamada (via axios ou via fetch) direcionada ao
 * backend do ClickHub passa a receber automaticamente o header
 * Authorization, sem precisar alterar cada arquivo que já faz a chamada.
 *
 * Deve ser importado UMA VEZ, o mais cedo possível (ver App.js), antes de
 * qualquer chamada à API acontecer.
 */
import axios from "axios";
import authService from "./authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ======================
// AXIOS - interceptor global
// ======================
axios.interceptors.request.use(
  (config) => {
    // Só anexa o token em chamadas para o próprio backend do ClickHub -
    // nunca em chamadas para outros domínios (ex.: serviços de terceiros).
    if (config.url && BACKEND_URL && config.url.startsWith(BACKEND_URL)) {
      const token = authService.getToken();
      if (token && !config.headers?.Authorization) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Se o backend responder 401, tenta renovar o token uma vez e repete a
// chamada original - mesmo comportamento que authService.fetchAuth já
// tinha, agora também vale para todo o app que usa axios.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const isBackendCall =
      originalRequest?.url && BACKEND_URL && originalRequest.url.startsWith(BACKEND_URL);

    if (error.response?.status === 401 && isBackendCall && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        await authService.refreshToken();
        const token = authService.getToken();
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      } catch (refreshError) {
        authService.limparTokens();
        return Promise.reject(error);
      }
    }
    return Promise.reject(error);
  }
);

// ======================
// FETCH - wrapper global
// ======================
// Guarda a implementação original antes de sobrescrever, para nunca
// perder a capacidade de fazer um fetch "cru" quando necessário.
const fetchOriginal = window.fetch.bind(window);

window.fetch = async (input, init = {}) => {
  const url = typeof input === "string" ? input : input?.url || "";
  const ehChamadaBackend = BACKEND_URL && url.startsWith(BACKEND_URL);

  if (ehChamadaBackend) {
    const token = authService.getToken();
    const jaTemAuth =
      init.headers && (init.headers["Authorization"] || init.headers["authorization"]);

    if (token && !jaTemAuth) {
      init = {
        ...init,
        headers: {
          ...(init.headers || {}),
          Authorization: `Bearer ${token}`,
        },
      };
    }
  }

  let response = await fetchOriginal(input, init);

  // Mesmo tratamento de renovação automática de token em caso de 401,
  // para chamadas feitas via fetch puro (ex.: carteiraService).
  if (ehChamadaBackend && response.status === 401 && authService.getToken()) {
    try {
      await authService.refreshToken();
      const novoToken = authService.getToken();
      init = {
        ...init,
        headers: { ...(init.headers || {}), Authorization: `Bearer ${novoToken}` },
      };
      response = await fetchOriginal(input, init);
    } catch (refreshError) {
      authService.limparTokens();
    }
  }

  return response;
};
